// AdminUnknownUsersManager.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Grid,
  Tooltip,
  Alert,
  Drawer,
  Divider,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WcIcon from '@mui/icons-material/Wc';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';
import BadgeIcon from '@mui/icons-material/Badge';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InputAdornment from '@mui/material/InputAdornment';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import api from '../../../API/axiosInstance';
import { API_ENDPOINTS } from '../../../config/api';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import safeToast from 'utils/safeToast';

ModuleRegistry.registerModules([AllCommunityModule]);
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const PRIMARY = '#2e7d32';
const PRIMARY_DARK = '#1b5e20';
const PRIMARY_SOFT = 'rgba(46, 125, 50, 0.08)';
const PRIMARY_BORDER = 'rgba(46, 125, 50, 0.22)';
const AUTOCOMPLETE_POPPER_SX = { zIndex: 10050 };

const pickField = (row, ...keys) => {
  if (!row || typeof row !== 'object') return null;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key) && row[key] != null && String(row[key]).trim() !== '') {
      return row[key];
    }
    const pascal = key.charAt(0).toUpperCase() + key.slice(1);
    if (Object.prototype.hasOwnProperty.call(row, pascal) && row[pascal] != null && String(row[pascal]).trim() !== '') {
      return row[pascal];
    }
  }
  return null;
};

const normalizeRegistrationRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  const firstName = pickField(row, 'firstName');
  const lastName = pickField(row, 'lastName');
  const fullName =
    pickField(row, 'fullName') ||
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    null;

  return {
    ...row,
    registrationId: pickField(row, 'registrationId') ?? row.registrationId,
    fullName,
    firstName,
    lastName,
    gender: pickField(row, 'gender'),
    dateOfBirth: pickField(row, 'dateOfBirth'),
    rollNumber: pickField(row, 'rollNumber'),
    email: pickField(row, 'email'),
    mobile: pickField(row, 'mobile'),
    whatsAppNumber: pickField(row, 'whatsAppNumber', 'whatsappNumber'),
    batchYear: pickField(row, 'batchYear', 'batch'),
    collegeStudied: pickField(row, 'collegeStudied'),
    courseStudied: pickField(row, 'courseStudied'),
    branch: pickField(row, 'branch', 'department'),
    category: pickField(row, 'category'),
    domain: pickField(row, 'domain'),
    presentStatus: pickField(row, 'presentStatus'),
    organization: pickField(row, 'organization'),
    designation: pickField(row, 'designation'),
    location: pickField(row, 'location'),
    availability: pickField(row, 'availability'),
    status: pickField(row, 'status') || row.status || 'Pending',
    verifiedByName: pickField(row, 'verifiedByName'),
    verifiedDate: pickField(row, 'verifiedDate'),
    rejectionReason: pickField(row, 'rejectionReason'),
    remarks: pickField(row, 'remarks'),
    studentId: pickField(row, 'studentId'),
    createdDate: pickField(row, 'createdDate') ?? row.createdDate,
    modifiedDate: pickField(row, 'modifiedDate'),
  };
};

const normalizeRegistrationList = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.result)
        ? payload.result
        : Array.isArray(payload?.Data)
          ? payload.Data
          : [];
  return list.map(normalizeRegistrationRow);
};

const EXPORT_COLUMNS = [
  { key: 'fullName', label: 'Name' },
  { key: 'gender', label: 'Gender' },
  { key: 'dateOfBirth', label: 'Date of Birth' },
  { key: 'rollNumber', label: 'Roll Number' },
  { key: 'email', label: 'Email' },
  { key: 'whatsAppNumber', label: 'WhatsApp' },
  { key: 'batchYear', label: 'Batch Year' },
  { key: 'collegeStudied', label: 'College' },
  { key: 'branch', label: 'Branch / Department' },
  { key: 'courseStudied', label: 'Course' },
  { key: 'category', label: 'Category' },
  { key: 'presentStatus', label: 'Present Status' },
  { key: 'domain', label: 'Domain' },
  { key: 'organization', label: 'Organization' },
  { key: 'designation', label: 'Designation' },
  { key: 'location', label: 'Location' },
  { key: 'availability', label: 'Availability' },
  { key: 'status', label: 'Status' },
  { key: 'createdDate', label: 'Submitted Date' },
  { key: 'verifiedByName', label: 'Verified By' },
  { key: 'verifiedDate', label: 'Verified Date' },
  { key: 'rejectionReason', label: 'Rejection Reason' },
  { key: 'remarks', label: 'Remarks' },
];

const PDF_EXPORT_KEYS = [
  'fullName',
  'gender',
  'batchYear',
  'branch',
  'courseStudied',
  'category',
  'organization',
  'designation',
  'location',
  'status',
  'createdDate',
];

const EMPTY_REPORT_FILTERS = {
  searchText: '',
  branches: [],
  genders: [],
  batchYears: [],
  colleges: [],
  courses: [],
  presentStatuses: [],
  organizations: [],
  designations: [],
  locations: [],
  availabilities: [],
  statuses: [],
  verifiedByNames: [],
  dateFrom: '',
  dateTo: '',
  verifiedDateFrom: '',
  verifiedDateTo: '',
};

const normalizeText = (value) => (value == null ? '' : String(value).trim());

const compactKey = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');

const titleCaseWords = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

/** Shared academic aliases for department / branch / course free-text values */
const ACADEMIC_GROUPS = [
  {
    key: 'dept:cse',
    label: 'Computer Science & Engineering (CSE)',
    match: (c) =>
      c.includes('computerscience') ||
      c === 'cse' ||
      c === 'cs' ||
      (c.includes('cse') && c.includes('computer')),
  },
  {
    key: 'dept:it',
    label: 'Information Technology (IT)',
    match: (c) => c === 'it' || c.includes('informationtechnology'),
  },
  {
    key: 'dept:ece',
    label: 'Electronics & Communication (ECE)',
    match: (c) =>
      c === 'ece' ||
      c.includes('electronicsandcommunication') ||
      c.includes('electronicscommunication'),
  },
  {
    key: 'dept:eee',
    label: 'Electrical & Electronics (EEE)',
    match: (c) =>
      c === 'eee' ||
      c.includes('electricalandelectronics') ||
      c.includes('electricalelectronics') ||
      (c.includes('eee') && (c.includes('be') || c.includes('btech') || c.includes('electrical'))),
  },
  {
    key: 'dept:biotech',
    label: 'Biotechnology',
    match: (c) => c.includes('biotech') || c.includes('biotechnology'),
  },
  {
    key: 'dept:foodtech',
    label: 'Food Technology',
    match: (c) => c.includes('foodtech') || c.includes('foodtechnology'),
  },
  {
    key: 'dept:nano',
    label: 'Nano Science & Technology',
    match: (c) => c.includes('nano'),
  },
  {
    key: 'dept:textile',
    label: 'Textile Technology',
    match: (c) => c.includes('textile'),
  },
  {
    key: 'dept:mech',
    label: 'Mechanical Engineering',
    match: (c) => c === 'mech' || c.includes('mechanical'),
  },
  {
    key: 'dept:civil',
    label: 'Civil Engineering',
    match: (c) => c === 'civil' || c.includes('civilengineering'),
  },
  {
    key: 'dept:aids',
    label: 'AI & Data Science',
    match: (c) => c.includes('artificialintelligence') || c.includes('datascience') || c === 'aids' || c === 'aiml',
  },
  {
    key: 'course:btech',
    label: 'B.Tech',
    match: (c) =>
      c === 'btech' ||
      c === 'btechnology' ||
      c.includes('bacheloroftechnology') ||
      c === 'btechdegree',
  },
  {
    key: 'course:be',
    label: 'B.E',
    match: (c) =>
      c === 'be' ||
      c === 'bachelorofengineering' ||
      c === 'bengineering',
  },
  {
    key: 'course:mtech',
    label: 'M.Tech',
    match: (c) => c === 'mtech' || c.includes('masteroftechnology'),
  },
  {
    key: 'course:me',
    label: 'M.E',
    match: (c) => c === 'me' || c.includes('masterofengineering'),
  },
  {
    key: 'course:mba',
    label: 'MBA',
    match: (c) => c === 'mba' || c.includes('masterofbusiness'),
  },
  {
    key: 'course:mca',
    label: 'MCA',
    match: (c) => c === 'mca' || c.includes('masterofcomputer'),
  },
];

const groupCollege = (value) => {
  const raw = normalizeText(value);
  if (!raw) return null;
  const c = compactKey(raw);
  if (
    c === 'ksrct' ||
    c === 'ksr' ||
    c.includes('ksrangasamy') ||
    c.includes('ksrangaswamy') ||
    c.includes('ksrcollegeoftechnology') ||
    (c.includes('ksr') && c.includes('technology')) ||
    (c.includes('rangasamy') && c.includes('technology')) ||
    (c.includes('rangaswamy') && c.includes('technology'))
  ) {
    return {
      key: 'college:ksrct',
      label: 'K.S. Rangasamy College of Technology (KSRCT)',
    };
  }
  return { key: `college:${c}`, label: titleCaseWords(raw) };
};

const groupAcademic = (value, prefix) => {
  const raw = normalizeText(value);
  if (!raw) return null;
  const c = compactKey(raw);

  for (const group of ACADEMIC_GROUPS) {
    if (group.match(c)) {
      return { key: group.key, label: group.label };
    }
  }

  return { key: `${prefix}:${c}`, label: titleCaseWords(raw) };
};

const groupBranch = (value) => groupAcademic(value, 'branch');
const groupCourse = (value) => groupAcademic(value, 'course');

const groupPresentStatus = (value) => {
  const raw = normalizeText(value);
  if (!raw) return null;
  const c = compactKey(raw);
  if (['working', 'work', 'employed', 'employee', 'job', 'jobs'].some((k) => c === k || c.includes(k))) {
    return { key: 'status:working', label: 'Working / Employed' };
  }
  if (c.includes('student') || c.includes('studying') || c.includes('highereducation')) {
    return { key: 'status:student', label: 'Student / Higher Education' };
  }
  if (c.includes('business') || c.includes('entrepreneur') || c.includes('self')) {
    return { key: 'status:business', label: 'Business / Self Employed' };
  }
  if (c.includes('freelance')) {
    return { key: 'status:freelance', label: 'Freelancing' };
  }
  return { key: `status:${c}`, label: titleCaseWords(raw) };
};

const groupLocation = (value) => {
  const raw = normalizeText(value);
  if (!raw) return null;
  const c = compactKey(raw);
  const aliases = {
    bengaluru: 'Bengaluru',
    bangalore: 'Bengaluru',
    banglore: 'Bengaluru',
    chennai: 'Chennai',
    madras: 'Chennai',
    coimbatore: 'Coimbatore',
    salem: 'Salem',
    hosur: 'Hosur',
    tiruchengode: 'Tiruchengode',
    trichy: 'Tiruchirappalli',
    tiruchirappalli: 'Tiruchirappalli',
    mohali: 'Mohali',
  };
  if (aliases[c]) {
    return { key: `loc:${aliases[c].toLowerCase()}`, label: aliases[c] };
  }
  return { key: `loc:${c}`, label: titleCaseWords(raw) };
};

const groupAvailability = (value) => {
  const raw = normalizeText(value);
  if (!raw) return null;
  const c = compactKey(raw);
  if (c === 'india' || c.includes('india')) {
    return { key: 'avail:india', label: 'India' };
  }
  if (c === 'available' || c.includes('available')) {
    return { key: 'avail:available', label: 'Available' };
  }
  return { key: `avail:${c}`, label: titleCaseWords(raw) };
};

const groupGeneric = (value, prefix) => {
  const raw = normalizeText(value);
  if (!raw) return null;
  const c = compactKey(raw);
  return { key: `${prefix}:${c}`, label: titleCaseWords(raw) };
};

const GROUPERS = {
  collegeStudied: groupCollege,
  branch: groupBranch,
  courseStudied: groupCourse,
  presentStatus: groupPresentStatus,
  location: groupLocation,
  availability: groupAvailability,
  organization: (v) => groupGeneric(v, 'org'),
  designation: (v) => groupGeneric(v, 'desig'),
  gender: (v) => groupGeneric(v, 'gender'),
  batchYear: (v) => groupGeneric(v, 'batch'),
  status: (v) => groupGeneric(v, 'regstatus'),
  verifiedByName: (v) => groupGeneric(v, 'verifier'),
};

const buildGroupedOptions = (data, field) => {
  const grouper = GROUPERS[field] || ((v) => groupGeneric(v, field));
  const pascal = field.charAt(0).toUpperCase() + field.slice(1);
  const map = new Map();

  data.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const raw = row[field] ?? row[pascal];
    const grouped = grouper(raw);
    if (!grouped) return;
    const existing = map.get(grouped.key);
    if (existing) {
      existing.count += 1;
      if (raw && !existing.variants.includes(normalizeText(raw))) {
        existing.variants.push(normalizeText(raw));
      }
    } else {
      map.set(grouped.key, {
        key: grouped.key,
        label: grouped.label,
        count: 1,
        variants: raw ? [normalizeText(raw)] : [],
      });
    }
  });

  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
};

const matchesGroupedFilter = (selectedKeys, rawValue, field) => {
  if (!selectedKeys || selectedKeys.length === 0) return true;
  const grouper = GROUPERS[field] || ((v) => groupGeneric(v, field));
  const grouped = grouper(rawValue);
  if (!grouped) return false;
  return selectedKeys.includes(grouped.key);
};

const FilterSectionTitle = ({ icon, title }) => (
  <Box display="flex" alignItems="center" gap={1} mt={0.5} mb={0.25}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '8px',
        bgcolor: PRIMARY_SOFT,
        color: PRIMARY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Typography variant="subtitle2" fontWeight={700} color={PRIMARY_DARK}>
      {title}
    </Typography>
  </Box>
);

FilterSectionTitle.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};

const ReportMultiSelect = ({ label, placeholder, options, value, onChange, icon }) => {
  const selectedOptions = (options || []).filter((opt) => (value || []).includes(opt.key));

  return (
    <Autocomplete
      multiple
      size="small"
      options={options || []}
      value={selectedOptions}
      onChange={(_, next) => onChange(next.map((opt) => opt.key))}
      getOptionLabel={(opt) => opt?.label || ''}
      isOptionEqualToValue={(a, b) => a?.key === b?.key}
      filterSelectedOptions
      disableCloseOnSelect
      ListboxProps={{ style: { maxHeight: 260 } }}
      renderOption={(props, opt) => (
        <li {...props} key={opt.key}>
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
            <Typography variant="body2" fontWeight={600}>
              {opt.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {opt.count} record{opt.count === 1 ? '' : 's'}
              {opt.variants?.length > 1 ? ` · ${opt.variants.length} name variants` : ''}
            </Typography>
          </Box>
        </li>
      )}
      componentsProps={{
        popper: {
          sx: AUTOCOMPLETE_POPPER_SX,
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport', padding: 8 },
            },
            {
              name: 'flip',
              options: { fallbackPlacements: ['top-start', 'bottom-end'] },
            },
          ],
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedOptions.length ? '' : placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {icon ? (
                  <InputAdornment position="start" sx={{ mr: 0.25 }}>
                    {icon}
                  </InputAdornment>
                ) : null}
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

ReportMultiSelect.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
      variants: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  icon: PropTypes.node,
};

ReportMultiSelect.defaultProps = {
  placeholder: 'Select',
  options: [],
  value: [],
  icon: null,
};

const AdminUnknownUsersManager = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [reportSourceData, setReportSourceData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState(EMPTY_REPORT_FILTERS);
  const [exporting, setExporting] = useState(false);

  const fetchRegistrations = useCallback(async (status = null) => {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.UNKNOWN_ALUMNI.GET_LIST(status));
      const data = normalizeRegistrationList(response?.data);
      setRowData(data);
    } catch (error) {
      console.error('Error fetching unknown alumni registrations:', error);
      safeToast.error('Failed to load registrations');
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchRegistrations(statusFilter === 'All' ? null : statusFilter);
    }
  }, [open, statusFilter, fetchRegistrations]);

  const reportFilterOptions = useMemo(() => ({
    branches: buildGroupedOptions(reportSourceData, 'branch'),
    genders: buildGroupedOptions(reportSourceData, 'gender'),
    batchYears: buildGroupedOptions(reportSourceData, 'batchYear'),
    colleges: buildGroupedOptions(reportSourceData, 'collegeStudied'),
    courses: buildGroupedOptions(reportSourceData, 'courseStudied'),
    presentStatuses: buildGroupedOptions(reportSourceData, 'presentStatus'),
    organizations: buildGroupedOptions(reportSourceData, 'organization'),
    designations: buildGroupedOptions(reportSourceData, 'designation'),
    locations: buildGroupedOptions(reportSourceData, 'location'),
    availabilities: buildGroupedOptions(reportSourceData, 'availability'),
    statuses: buildGroupedOptions(reportSourceData, 'status'),
    verifiedByNames: buildGroupedOptions(reportSourceData, 'verifiedByName'),
  }), [reportSourceData]);

  const filteredReportData = useMemo(() => {
    const fromDate = reportFilters.dateFrom ? new Date(`${reportFilters.dateFrom}T00:00:00`) : null;
    const toDate = reportFilters.dateTo ? new Date(`${reportFilters.dateTo}T23:59:59`) : null;
    const verifiedFrom = reportFilters.verifiedDateFrom
      ? new Date(`${reportFilters.verifiedDateFrom}T00:00:00`)
      : null;
    const verifiedTo = reportFilters.verifiedDateTo
      ? new Date(`${reportFilters.verifiedDateTo}T23:59:59`)
      : null;
    const search = normalizeText(reportFilters.searchText).toLowerCase();

    return reportSourceData.filter((row) => {
      if (search) {
        const haystack = [
          row.fullName,
          row.email,
          row.rollNumber,
          row.whatsAppNumber,
          row.firstName,
          row.lastName,
          row.organization,
        ]
          .map(normalizeText)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }

      if (!matchesGroupedFilter(reportFilters.branches, row.branch, 'branch')) return false;
      if (!matchesGroupedFilter(reportFilters.genders, row.gender, 'gender')) return false;
      if (!matchesGroupedFilter(reportFilters.batchYears, row.batchYear, 'batchYear')) return false;
      if (!matchesGroupedFilter(reportFilters.colleges, row.collegeStudied, 'collegeStudied')) return false;
      if (!matchesGroupedFilter(reportFilters.courses, row.courseStudied, 'courseStudied')) return false;
      if (!matchesGroupedFilter(reportFilters.presentStatuses, row.presentStatus, 'presentStatus')) return false;
      if (!matchesGroupedFilter(reportFilters.organizations, row.organization, 'organization')) return false;
      if (!matchesGroupedFilter(reportFilters.designations, row.designation, 'designation')) return false;
      if (!matchesGroupedFilter(reportFilters.locations, row.location, 'location')) return false;
      if (!matchesGroupedFilter(reportFilters.availabilities, row.availability, 'availability')) return false;
      if (!matchesGroupedFilter(reportFilters.statuses, row.status, 'status')) return false;
      if (!matchesGroupedFilter(reportFilters.verifiedByNames, row.verifiedByName, 'verifiedByName')) return false;

      if (fromDate || toDate) {
        if (!row.createdDate) return false;
        const created = new Date(row.createdDate);
        if (Number.isNaN(created.getTime())) return false;
        if (fromDate && created < fromDate) return false;
        if (toDate && created > toDate) return false;
      }

      if (verifiedFrom || verifiedTo) {
        if (!row.verifiedDate) return false;
        const verified = new Date(row.verifiedDate);
        if (Number.isNaN(verified.getTime())) return false;
        if (verifiedFrom && verified < verifiedFrom) return false;
        if (verifiedTo && verified > verifiedTo) return false;
      }

      return true;
    });
  }, [reportSourceData, reportFilters]);

  const openReportDrawer = useCallback(async () => {
    setReportDrawerOpen(true);
    setReportSourceData(Array.isArray(rowData) ? rowData : []);
    setReportLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.UNKNOWN_ALUMNI.GET_LIST(null));
      const data = normalizeRegistrationList(response?.data);
      setReportSourceData(data.length ? data : (Array.isArray(rowData) ? rowData : []));
    } catch (error) {
      console.error('Error loading report data:', error);
      safeToast.error('Failed to load report data');
      setReportSourceData(Array.isArray(rowData) ? rowData : []);
    } finally {
      setReportLoading(false);
    }
  }, [rowData]);

  const clearReportFilters = useCallback(() => {
    setReportFilters(EMPTY_REPORT_FILTERS);
  }, []);

  const updateReportFilter = useCallback((key, value) => {
    setReportFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const formatExportValue = useCallback((row, key) => {
    const value = row?.[key] ?? pickField(row, key);
    if (key === 'createdDate' || key === 'verifiedDate' || key === 'dateOfBirth') {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    return value == null ? '' : String(value).trim();
  }, []);

  const buildExportRows = useCallback((data) => (
    data.map((row, index) => {
      const normalized = normalizeRegistrationRow(row);
      const exportRow = { 'S.No': index + 1 };
      EXPORT_COLUMNS.forEach(({ key, label }) => {
        exportRow[label] = formatExportValue(normalized, key);
      });
      return exportRow;
    })
  ), [formatExportValue]);

  const getExportFileName = useCallback((extension) => {
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    return `Alumni-Registration-Report-${ts}.${extension}`;
  }, []);

  const handleExportExcel = useCallback(() => {
    if (filteredReportData.length === 0) {
      safeToast.warn('No records match the selected filters');
      return;
    }

    try {
      setExporting(true);
      const rows = buildExportRows(filteredReportData);
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Registrations');
      const colWidths = Object.keys(rows[0] || {}).map((key) => ({
        wch: Math.min(40, Math.max(12, String(key).length + 2)),
      }));
      worksheet['!cols'] = colWidths;
      XLSX.writeFile(workbook, getExportFileName('xlsx'));
      safeToast.success(`Excel exported (${filteredReportData.length} records)`);
    } catch (error) {
      console.error('Excel export error:', error);
      safeToast.error(error?.message || 'Excel export failed');
    } finally {
      setExporting(false);
    }
  }, [filteredReportData, buildExportRows, getExportFileName]);

  const handleExportPdf = useCallback(async () => {
    if (filteredReportData.length === 0) {
      safeToast.warn('No records match the selected filters');
      return;
    }

    try {
      setExporting(true);
      const jspdfModule = await import('jspdf');
      const jsPDF = jspdfModule.jsPDF || jspdfModule.default?.jsPDF || jspdfModule.default;
      const autoTableModule = await import('jspdf-autotable');
      const autoTable =
        autoTableModule.default ||
        autoTableModule.autoTable ||
        (typeof autoTableModule === 'function' ? autoTableModule : null);

      if (!jsPDF) {
        throw new Error('jsPDF failed to load');
      }

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(33, 33, 33);
      doc.text('Alumni Registration Report', 40, 36);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 52);
      doc.text(`Total Records: ${filteredReportData.length}`, 40, 66);

      const pdfColumns = EXPORT_COLUMNS.filter((col) => PDF_EXPORT_KEYS.includes(col.key));
      const body = filteredReportData.map((row) => {
        const normalized = normalizeRegistrationRow(row);
        return pdfColumns.map((col) => formatExportValue(normalized, col.key));
      });

      const tableOptions = {
        startY: 78,
        head: [pdfColumns.map((col) => col.label)],
        body,
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak', textColor: [33, 33, 33] },
        headStyles: { fillColor: [46, 125, 50], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [232, 245, 233] },
        margin: { left: 30, right: 30 },
      };

      if (typeof autoTable === 'function') {
        autoTable(doc, tableOptions);
      } else if (typeof doc.autoTable === 'function') {
        doc.autoTable(tableOptions);
      } else {
        throw new Error('jspdf-autotable is not available');
      }

      doc.save(getExportFileName('pdf'));
      safeToast.success(`PDF exported (${filteredReportData.length} records)`);
    } catch (error) {
      console.error('PDF export error:', error);
      safeToast.error(error?.message || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  }, [filteredReportData, formatExportValue, getExportFileName]);

  const formatDate = useCallback((value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }, []);

  const displayValue = useCallback((value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '—';
    return String(value).trim();
  }, []);

  const actionCellRenderer = useCallback((params) => {
    const isPending = params.data?.status === 'Pending';

    return (
      <Box display="flex" gap={0.75} alignItems="center" justifyContent="center" height="100%">
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedRegistration(params.data);
              setDetailsDialogOpen(true);
            }}
            sx={{
              color: '#455a64',
              bgcolor: 'rgba(69, 90, 100, 0.08)',
              borderRadius: 1,
              p: 0.5,
              '&:hover': { bgcolor: 'rgba(69, 90, 100, 0.16)' },
            }}
          >
            <VisibilityIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        {isPending ? (
          <>
            <Tooltip title="Approve">
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => {
                  setSelectedRegistration(params.data);
                  setApproveDialogOpen(true);
                }}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0.25,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: 'none',
                  lineHeight: 1.4,
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                Approve
              </Button>
            </Tooltip>
            <Tooltip title="Reject">
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<CancelIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => {
                  setSelectedRegistration(params.data);
                  setRejectDialogOpen(true);
                }}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0.25,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: 'none',
                  lineHeight: 1.4,
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                Reject
              </Button>
            </Tooltip>
          </>
        ) : (
          <Chip
            label={params.data.status}
            color={params.data.status === 'Approved' ? 'success' : 'error'}
            size="small"
            sx={{ fontSize: '0.68rem', height: 22, fontWeight: 600 }}
          />
        )}
      </Box>
    );
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: 'S.No',
      colId: 'sno',
      valueGetter: (params) => {
        const gridApi = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        if (gridApi?.paginationGetPageSize) {
          const page = gridApi.paginationGetCurrentPage?.() ?? 0;
          const pageSize = gridApi.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 70,
      minWidth: 70,
      maxWidth: 80,
      pinned: 'left',
      lockPinned: true,
      resizable: false,
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressSizeToFit: true,
      cellClass: 'center-cell',
      headerClass: 'alumni-reg-header center-header',
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      width: 220,
      minWidth: 220,
      maxWidth: 240,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: actionCellRenderer,
      sortable: false,
      filter: false,
      floatingFilter: false,
      resizable: false,
      suppressSizeToFit: true,
      cellClass: 'actions-cell',
    },
    {
      field: 'fullName',
      headerName: 'Name',
      width: 170,
      minWidth: 140,
      pinned: 'left',
      lockPinned: true,
      tooltipField: 'fullName',
      cellClass: 'name-cell ellipsis-cell',
    },
    {
      field: 'gender',
      headerName: 'Gender',
      width: 100,
      minWidth: 90,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'rollNumber',
      headerName: 'Roll Number',
      width: 140,
      minWidth: 120,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
      tooltipValueGetter: (params) => displayValue(params.value),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
      minWidth: 180,
      tooltipField: 'email',
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'whatsAppNumber',
      headerName: 'WhatsApp',
      width: 130,
      minWidth: 120,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
      tooltipValueGetter: (params) => displayValue(params.value),
    },
    {
      field: 'batchYear',
      headerName: 'Batch',
      width: 120,
      minWidth: 100,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'collegeStudied',
      headerName: 'College',
      width: 240,
      minWidth: 200,
      tooltipField: 'collegeStudied',
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'branch',
      headerName: 'Branch',
      width: 220,
      minWidth: 180,
      tooltipField: 'branch',
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'courseStudied',
      headerName: 'Course',
      width: 220,
      minWidth: 180,
      tooltipField: 'courseStudied',
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
      minWidth: 100,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'presentStatus',
      headerName: 'Present Status',
      width: 140,
      minWidth: 120,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
      tooltipValueGetter: (params) => displayValue(params.value),
    },
    {
      field: 'domain',
      headerName: 'Domain',
      width: 160,
      minWidth: 130,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
      tooltipValueGetter: (params) => displayValue(params.value),
    },
    {
      field: 'organization',
      headerName: 'Organization',
      width: 200,
      minWidth: 160,
      tooltipField: 'organization',
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'designation',
      headerName: 'Designation',
      width: 170,
      minWidth: 140,
      tooltipField: 'designation',
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 140,
      minWidth: 120,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
      tooltipValueGetter: (params) => displayValue(params.value),
    },
    {
      field: 'availability',
      headerName: 'Availability',
      width: 120,
      minWidth: 110,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      minWidth: 110,
      cellClass: 'status-cell',
      cellRenderer: (params) => {
        const status = params.value || '—';
        const color = status === 'Approved' ? 'success' : status === 'Rejected' ? 'error' : 'warning';
        return (
          <Chip
            label={status}
            color={color}
            size="small"
            sx={{ fontSize: '0.68rem', height: 22, fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'createdDate',
      headerName: 'Submitted',
      width: 120,
      minWidth: 110,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => formatDate(params.value),
      filterValueGetter: (params) => formatDate(params.data?.createdDate),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'verifiedByName',
      headerName: 'Verified By',
      width: 140,
      minWidth: 120,
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
      tooltipValueGetter: (params) => displayValue(params.value),
    },
    {
      field: 'verifiedDate',
      headerName: 'Verified Date',
      width: 130,
      minWidth: 120,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => formatDate(params.value),
      filterValueGetter: (params) => formatDate(params.data?.verifiedDate),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'rejectionReason',
      headerName: 'Rejection Reason',
      width: 200,
      minWidth: 160,
      tooltipField: 'rejectionReason',
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      width: 180,
      minWidth: 140,
      tooltipField: 'remarks',
      valueFormatter: (params) => displayValue(params.value),
      cellClass: 'ellipsis-cell',
    },
  ], [actionCellRenderer, displayValue, formatDate]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    minWidth: 100,
    wrapText: false,
    autoHeight: false,
    suppressMovable: false,
    tooltipShowDelay: 300,
    headerClass: 'alumni-reg-header',
  }), []);

  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
  }, []);

  const handleApprove = useCallback(async () => {
    if (!selectedRegistration) return;

    setProcessing(true);
    try {
      const response = await api.post(API_ENDPOINTS.UNKNOWN_ALUMNI.VERIFY, {
        registrationId: selectedRegistration.registrationId,
        action: 'Approve',
        verifiedBy: 1, // TODO: Get from auth context
        remarks: remarks,
      });

      if (response.data?.success) {
        safeToast.success(`Registration approved successfully! ${response.data.studentId ? `StudentId: ${response.data.studentId}` : ''}`);
        setApproveDialogOpen(false);
        setRemarks('');
        setSelectedRegistration(null);
        fetchRegistrations(statusFilter === 'All' ? null : statusFilter);
      } else {
        throw new Error(response.data?.message || 'Failed to approve registration');
      }
    } catch (error) {
      console.error('Error approving registration:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to approve registration';
      safeToast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  }, [selectedRegistration, remarks, statusFilter, fetchRegistrations]);

  const handleReject = useCallback(async () => {
    if (!selectedRegistration || !rejectionReason.trim()) {
      safeToast.warn('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post(API_ENDPOINTS.UNKNOWN_ALUMNI.VERIFY, {
        registrationId: selectedRegistration.registrationId,
        action: 'Reject',
        verifiedBy: 1, // TODO: Get from auth context
        rejectionReason: rejectionReason,
        remarks: remarks,
      });

      if (response.data?.success) {
        safeToast.success('Registration rejected successfully');
        setRejectDialogOpen(false);
        setRejectionReason('');
        setRemarks('');
        setSelectedRegistration(null);
        fetchRegistrations(statusFilter === 'All' ? null : statusFilter);
      } else {
        throw new Error(response.data?.message || 'Failed to reject registration');
      }
    } catch (error) {
      console.error('Error rejecting registration:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reject registration';
      safeToast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  }, [selectedRegistration, rejectionReason, remarks, statusFilter, fetchRegistrations]);

  const stats = useMemo(() => {
    const total = rowData.length;
    const pending = rowData.filter(r => r.status === 'Pending').length;
    const approved = rowData.filter(r => r.status === 'Approved').length;
    const rejected = rowData.filter(r => r.status === 'Rejected').length;
    return { total, pending, approved, rejected };
  }, [rowData]);

  if (loading && rowData.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
            <CircularProgress size={50} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="xl" 
      fullScreen
      PaperProps={{
        sx: {
          background: '#ffffff',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: '#ffffff',
          borderBottom: '1px solid #e0e0e0',
          py: 2,
          px: 3,
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold" color="dark">
            Alumni Registration Management
          </MDTypography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#546e7a' }}>
            <CloseIcon />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, bgcolor: '#ffffff' }}>
        <MDBox display="flex" flexDirection="column" gap={2}>
          {/* Statistics */}
          <Grid container spacing={1.5}>
            {[
              { label: 'Total', value: stats.total, color: '#37474f', bg: '#ffffff', border: '#cfd8dc' },
              { label: 'Pending', value: stats.pending, color: '#ef6c00', bg: '#ffffff', border: '#ffe0b2' },
              { label: 'Approved', value: stats.approved, color: '#2e7d32', bg: '#ffffff', border: '#c8e6c9' },
              { label: 'Rejected', value: stats.rejected, color: '#c62828', bg: '#ffffff', border: '#ffcdd2' },
            ].map((item) => (
              <Grid item xs={6} sm={3} key={item.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${item.border}`,
                    bgcolor: item.bg,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ color: item.color, fontWeight: 700, letterSpacing: 0.4 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h5" sx={{ color: item.color, fontWeight: 800, lineHeight: 1.2 }}>
                    {item.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Filters */}
          <Paper elevation={0} sx={{ p: 1.75, border: '1px solid #e0e6ed', borderRadius: 2, bgcolor: '#fff' }}>
            <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Autocomplete
                size="small"
                sx={{ minWidth: 220 }}
                options={['All', 'Pending', 'Approved', 'Rejected']}
                value={statusFilter}
                onChange={(event, newValue) => {
                  if (newValue) setStatusFilter(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Status" />
                )}
              />
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => fetchRegistrations(statusFilter === 'All' ? null : statusFilter)}
              >
                Refresh
              </MDButton>
              <MDButton
                variant="gradient"
                color="success"
                size="small"
                startIcon={<AssessmentIcon />}
                onClick={openReportDrawer}
              >
                Report / Export
              </MDButton>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                Showing all registration fields · scroll horizontally for more
              </Typography>
            </MDBox>
          </Paper>

          {/* Data Grid */}
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #e0e6ed',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#fff',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
            }}
          >
            <div className="ag-theme-alpine" style={{ height: 'calc(100vh - 280px)', width: '100%' }}>
              <style>{`
                .ag-theme-alpine {
                  --ag-font-family: "Roboto", "Helvetica", "Arial", sans-serif;
                  --ag-border-color: #e0e0e0;
                  --ag-header-background-color: #ffffff;
                  --ag-header-foreground-color: #37474f;
                  --ag-odd-row-background-color: #fafafa;
                  --ag-row-hover-color: #f5f5f5;
                  --ag-selected-row-background-color: #eeeeee;
                  --ag-alpine-active-color: #607d8b;
                  --ag-background-color: #ffffff;
                  --ag-cell-horizontal-padding: 10px;
                }
                .ag-theme-alpine .alumni-reg-header,
                .ag-theme-alpine .ag-header-cell-text {
                  font-size: 0.72rem !important;
                  font-weight: 700 !important;
                  letter-spacing: 0.02em;
                  color: #37474f !important;
                }
                .ag-theme-alpine .ag-header {
                  border-bottom: 1px solid #e0e0e0;
                }
                .ag-theme-alpine .ag-header-cell {
                  border-right: 1px solid #eeeeee;
                }
                .ag-theme-alpine .ag-cell {
                  font-size: 0.78rem !important;
                  border-right: 1px solid #f0f0f0;
                  color: #263238;
                  overflow: hidden !important;
                  text-overflow: ellipsis !important;
                  white-space: nowrap !important;
                  line-height: 42px !important;
                  display: block !important;
                }
                .ag-theme-alpine .ag-cell-wrapper,
                .ag-theme-alpine .ag-cell-value {
                  overflow: hidden !important;
                  text-overflow: ellipsis !important;
                  white-space: nowrap !important;
                  width: 100%;
                }
                .ag-theme-alpine .ellipsis-cell {
                  overflow: hidden !important;
                }
                .ag-theme-alpine .name-cell {
                  font-weight: 600 !important;
                  color: #263238 !important;
                }
                .ag-theme-alpine .center-cell {
                  text-align: center !important;
                  font-weight: 700 !important;
                  color: #37474f !important;
                }
                .ag-theme-alpine .center-header .ag-header-cell-label {
                  justify-content: center;
                }
                .ag-theme-alpine .actions-cell,
                .ag-theme-alpine .status-cell {
                  display: flex !important;
                  align-items: center !important;
                  overflow: visible !important;
                  white-space: normal !important;
                  line-height: normal !important;
                }
                .ag-theme-alpine .actions-cell .ag-cell-wrapper,
                .ag-theme-alpine .status-cell .ag-cell-wrapper {
                  overflow: visible !important;
                  width: 100%;
                }
                .ag-theme-alpine .ag-floating-filter-input input,
                .ag-theme-alpine .ag-floating-filter-body input {
                  font-size: 0.72rem !important;
                }
                .ag-theme-alpine .ag-pinned-left-header,
                .ag-theme-alpine .ag-pinned-left-cols-container {
                  border-right: 2px solid #cfd8dc;
                  box-shadow: 2px 0 6px rgba(0,0,0,0.04);
                }
                .ag-theme-alpine .ag-pinned-left-cols-container .ag-cell {
                  background: #fff;
                }
                .ag-theme-alpine .ag-row-odd .ag-pinned-left-cols-container .ag-cell {
                  background: #fafafa;
                }
                .ag-theme-alpine .ag-paging-panel {
                  font-size: 0.8rem;
                  border-top: 1px solid #e0e0e0;
                  background: #ffffff;
                }
              `}</style>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                animateRows={true}
                rowHeight={44}
                headerHeight={40}
                floatingFiltersHeight={36}
                pagination={true}
                paginationPageSize={25}
                paginationPageSizeSelector={[25, 50, 100]}
                tooltipShowDelay={300}
                suppressCellFocus={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
              />
            </div>
          </Paper>
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <MDButton onClick={onClose} color="dark" variant="outlined" size="small">
          Close
        </MDButton>
      </DialogActions>

      {/* Report Filter Drawer */}
      <Drawer
        anchor="right"
        open={reportDrawerOpen}
        onClose={() => setReportDrawerOpen(false)}
        ModalProps={{
          keepMounted: false,
          disablePortal: false,
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 5,
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 460 },
            bgcolor: '#ffffff',
            borderLeft: `1px solid ${PRIMARY_BORDER}`,
            zIndex: (theme) => theme.zIndex.modal + 6,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              px: 2.5,
              py: 2,
              background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
              color: '#fff',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1.25}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,255,255,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FilterListIcon />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={800} color="#fff" lineHeight={1.2}>
                    Report Filters
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                    Department, date & advanced filters
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setReportDrawerOpen(false)} sx={{ color: '#fff' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: 2.5, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Paper
              elevation={0}
              sx={{
                p: 1.75,
                mb: 2,
                borderRadius: 2,
                border: `1px solid ${PRIMARY_BORDER}`,
                bgcolor: PRIMARY_SOFT,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <FactCheckIcon sx={{ color: PRIMARY, fontSize: 28 }} />
              <Box>
                <Typography variant="caption" fontWeight={700} color={PRIMARY_DARK}>
                  Matching Records
                </Typography>
                <Typography variant="h5" fontWeight={800} color={PRIMARY} lineHeight={1.15}>
                  {reportLoading ? '…' : filteredReportData.length}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    of {reportSourceData.length}
                  </Typography>
                </Typography>
              </Box>
            </Paper>

            <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
              {reportLoading && reportSourceData.length === 0 ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress size={36} sx={{ color: PRIMARY }} />
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  <FilterSectionTitle
                    icon={<PersonSearchIcon sx={{ fontSize: 16 }} />}
                    title="Search"
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Name / Email / Roll / WhatsApp"
                    placeholder="Type to search..."
                    value={reportFilters.searchText}
                    onChange={(e) => updateReportFilter('searchText', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FilterSectionTitle
                    icon={<AccountTreeIcon sx={{ fontSize: 16 }} />}
                    title="Department & Academic"
                  />
                  <ReportMultiSelect
                    label="Branch / Department"
                    placeholder="Select department"
                    options={reportFilterOptions.branches}
                    value={reportFilters.branches}
                    onChange={(value) => updateReportFilter('branches', value)}
                    icon={<AccountTreeIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="Course"
                    placeholder="Select course"
                    options={reportFilterOptions.courses}
                    value={reportFilters.courses}
                    onChange={(value) => updateReportFilter('courses', value)}
                    icon={<MenuBookIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="Batch Year"
                    placeholder="Select batch"
                    options={reportFilterOptions.batchYears}
                    value={reportFilters.batchYears}
                    onChange={(value) => updateReportFilter('batchYears', value)}
                    icon={<EventAvailableIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="College"
                    placeholder="Select college"
                    options={reportFilterOptions.colleges}
                    value={reportFilters.colleges}
                    onChange={(value) => updateReportFilter('colleges', value)}
                    icon={<SchoolIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />

                  <FilterSectionTitle
                    icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                    title="Date Range"
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Submitted From"
                    InputLabelProps={{ shrink: true }}
                    value={reportFilters.dateFrom}
                    onChange={(e) => updateReportFilter('dateFrom', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Submitted To"
                    InputLabelProps={{ shrink: true }}
                    value={reportFilters.dateTo}
                    onChange={(e) => updateReportFilter('dateTo', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Verified From"
                    InputLabelProps={{ shrink: true }}
                    value={reportFilters.verifiedDateFrom}
                    onChange={(e) => updateReportFilter('verifiedDateFrom', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VerifiedUserIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Verified To"
                    InputLabelProps={{ shrink: true }}
                    value={reportFilters.verifiedDateTo}
                    onChange={(e) => updateReportFilter('verifiedDateTo', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VerifiedUserIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FilterSectionTitle
                    icon={<BadgeIcon sx={{ fontSize: 16 }} />}
                    title="Profile & Status"
                  />
                  <ReportMultiSelect
                    label="Registration Status"
                    placeholder="Pending / Approved / Rejected"
                    options={reportFilterOptions.statuses}
                    value={reportFilters.statuses}
                    onChange={(value) => updateReportFilter('statuses', value)}
                    icon={<FactCheckIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="Gender"
                    placeholder="Select gender"
                    options={reportFilterOptions.genders}
                    value={reportFilters.genders}
                    onChange={(value) => updateReportFilter('genders', value)}
                    icon={<WcIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="Present Status"
                    placeholder="Working / Student / etc."
                    options={reportFilterOptions.presentStatuses}
                    value={reportFilters.presentStatuses}
                    onChange={(value) => updateReportFilter('presentStatuses', value)}
                    icon={<WorkIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="Verified By"
                    placeholder="Select verifier"
                    options={reportFilterOptions.verifiedByNames}
                    value={reportFilters.verifiedByNames}
                    onChange={(value) => updateReportFilter('verifiedByNames', value)}
                    icon={<VerifiedUserIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />

                  <FilterSectionTitle
                    icon={<BusinessIcon sx={{ fontSize: 16 }} />}
                    title="Career & Location"
                  />
                  <ReportMultiSelect
                    label="Organization"
                    placeholder="Select organization"
                    options={reportFilterOptions.organizations}
                    value={reportFilters.organizations}
                    onChange={(value) => updateReportFilter('organizations', value)}
                    icon={<BusinessIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="Designation"
                    placeholder="Select designation"
                    options={reportFilterOptions.designations}
                    value={reportFilters.designations}
                    onChange={(value) => updateReportFilter('designations', value)}
                    icon={<BadgeIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="Location"
                    placeholder="Select location"
                    options={reportFilterOptions.locations}
                    value={reportFilters.locations}
                    onChange={(value) => updateReportFilter('locations', value)}
                    icon={<PlaceIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                  <ReportMultiSelect
                    label="Availability"
                    placeholder="Select availability"
                    options={reportFilterOptions.availabilities}
                    value={reportFilters.availabilities}
                    onChange={(value) => updateReportFilter('availabilities', value)}
                    icon={<PublicIcon sx={{ color: PRIMARY, fontSize: 18 }} />}
                  />
                </Stack>
              )}
            </Box>

            <Divider sx={{ my: 2, borderColor: PRIMARY_BORDER }} />

            <Stack spacing={1.25}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearReportFilters}
                disabled={reportLoading || exporting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: PRIMARY,
                  borderColor: PRIMARY_BORDER,
                  '&:hover': { borderColor: PRIMARY, bgcolor: PRIMARY_SOFT },
                }}
              >
                Clear Filters
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={<TableChartIcon sx={{ color: '#fff !important' }} />}
                onClick={handleExportExcel}
                disabled={reportLoading || exporting || filteredReportData.length === 0}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: `${PRIMARY} !important`,
                  color: '#ffffff !important',
                  boxShadow: '0 4px 12px rgba(46,125,50,0.35)',
                  '&:hover': { bgcolor: `${PRIMARY_DARK} !important` },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(46,125,50,0.35) !important',
                    color: 'rgba(255,255,255,0.8) !important',
                  },
                }}
              >
                {exporting ? 'Exporting…' : 'Export Excel'}
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon sx={{ color: '#fff !important' }} />}
                onClick={handleExportPdf}
                disabled={reportLoading || exporting || filteredReportData.length === 0}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: `${PRIMARY_DARK} !important`,
                  color: '#ffffff !important',
                  boxShadow: '0 4px 12px rgba(27,94,32,0.35)',
                  '&:hover': { bgcolor: '#145218 !important' },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(27,94,32,0.35) !important',
                    color: 'rgba(255,255,255,0.8) !important',
                  },
                }}
              >
                {exporting ? 'Exporting…' : 'Export PDF'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => !processing && setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Registration</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
            Are you sure you want to approve this registration? The user will be moved to the Alumni database.
          </MDTypography>
          {selectedRegistration && (
            <MDTypography variant="body2" fontWeight="bold" color="dark">
              {selectedRegistration.fullName} ({selectedRegistration.email || selectedRegistration.mobile})
            </MDTypography>
          )}
          <TextField
            fullWidth
            label="Remarks (Optional)"
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button onClick={handleApprove} color="success" variant="contained" disabled={processing}>
            {processing ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => !processing && setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Registration</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this registration.
          </MDTypography>
          {selectedRegistration && (
            <MDTypography variant="body2" fontWeight="bold" color="dark" sx={{ mb: 2 }}>
              {selectedRegistration.fullName} ({selectedRegistration.email || selectedRegistration.mobile})
            </MDTypography>
          )}
          <TextField
            fullWidth
            label="Rejection Reason *"
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            error={!rejectionReason.trim()}
            helperText={!rejectionReason.trim() ? 'Rejection reason is required' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Remarks (Optional)"
            multiline
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button 
            onClick={handleReject} 
            color="error" 
            variant="contained" 
            disabled={processing || !rejectionReason.trim()}
          >
            {processing ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6, fontWeight: 700, color: '#263238' }}>
          Registration Details
          <IconButton
            onClick={() => setDetailsDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRegistration && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDTypography variant="h6" fontWeight="bold" color="dark">
                  {selectedRegistration.fullName}
                </MDTypography>
                <Chip
                  label={selectedRegistration.status}
                  color={selectedRegistration.status === 'Approved' ? 'success' : selectedRegistration.status === 'Rejected' ? 'error' : 'warning'}
                  size="small"
                  sx={{ mt: 1, fontWeight: 600 }}
                />
              </Grid>

              {[
                { label: 'Gender', value: selectedRegistration.gender },
                { label: 'Date of Birth', value: formatDate(selectedRegistration.dateOfBirth) },
                { label: 'Roll Number', value: selectedRegistration.rollNumber },
                { label: 'Email', value: selectedRegistration.email },
                { label: 'WhatsApp', value: selectedRegistration.whatsAppNumber },
                { label: 'Batch Year', value: selectedRegistration.batchYear },
                { label: 'College', value: selectedRegistration.collegeStudied },
                { label: 'Branch', value: selectedRegistration.branch },
                { label: 'Course', value: selectedRegistration.courseStudied },
                { label: 'Category', value: selectedRegistration.category },
                { label: 'Present Status', value: selectedRegistration.presentStatus },
                { label: 'Domain', value: selectedRegistration.domain },
                { label: 'Organization', value: selectedRegistration.organization },
                { label: 'Designation', value: selectedRegistration.designation },
                { label: 'Location', value: selectedRegistration.location },
                { label: 'Availability', value: selectedRegistration.availability },
                { label: 'Submitted', value: formatDate(selectedRegistration.createdDate) },
                { label: 'Verified By', value: selectedRegistration.verifiedByName },
                { label: 'Verified Date', value: formatDate(selectedRegistration.verifiedDate) },
                { label: 'Remarks', value: selectedRegistration.remarks },
              ].map((item) => (
                <Grid item xs={12} sm={6} key={item.label}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#263238' }}>
                    {item.value === null || item.value === undefined || item.value === ''
                      ? '—'
                      : item.value}
                  </Typography>
                </Grid>
              ))}

              {selectedRegistration.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <MDTypography variant="caption" fontWeight="bold">Rejection Reason:</MDTypography>
                    <MDTypography variant="body2">{selectedRegistration.rejectionReason}</MDTypography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

AdminUnknownUsersManager.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminUnknownUsersManager;

